import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { useDropzone } from 'react-dropzone';
import Grid from '@material-ui/core/Grid';
import AddIcon from '@material-ui/icons/Add';
import CardActionArea from '@material-ui/core/CardActionArea';
import FileCopy from '@material-ui/icons/FileCopy';
import Typography from '@material-ui/core/Typography';
import DeleteButton from './DeleteButton';

const propTypes = {
  onChange: PropTypes.func.isRequired,
  multiple: PropTypes.bool,
  label: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.shape({}),
    PropTypes.arrayOf(PropTypes.shape({})),
  ]),
};

const defaultProps = {
  multiple: false,
  label: '',
  value: [],
};

const useStyles = makeStyles(theme => ({
  paperRoot: {
    margin: theme.spacing(1, 0),
    flexBasis: 'auto',
    maxWidth: '100%',
  },
  root: {
    height: '100%',
  },
  thumbContainer: {
    border: '1px dashed rgba(0, 0, 0, 0.42)',
    borderRadius: 4,
    height: 120,
    outline: 'none',
    position: 'relative',
  },
  fileContainer: {
    height: '100%',
    padding: 8,
  },
  deleteButton: {
    position: 'absolute',
    right: 0,
  },
}));

const baseStyle = {
  border: '1px dashed rgba(0, 0, 0, 0.42)',
  borderRadius: 4,
  cursor: 'pointer',
  height: 120,
  outline: 'none',
};

const img = {
  boxSizing: 'border-box',
  height: '100%',
  objectFit: 'contain',
  padding: 8,
  width: '100%',
};

function Dropzone(props) {
  const [files, setFiles] = useState([]);
  const classes = useStyles();
  const { label, multiple, value } = props;

  useEffect(() => {
    if (!value) {
      setFiles([]);
    }
  }, [value]);

  const {
    getRootProps,
    getInputProps,
  } = useDropzone({
    multiple,
    onDrop: (acceptedFiles) => {
      const aux = acceptedFiles.filter((el) => {
        const exists = files.some(file => file.name === el.name);
        return !exists;
      });
      const newFile = aux.map(file => Object.assign(file, {
        preview: file.type.includes('image/') ? URL.createObjectURL(file) : '',
      }));
      const newFiles = [...files, ...newFile];
      setFiles(newFiles);
      props.onChange(newFiles);
    },
  });

  const removeFile = file => () => {
    const newFiles = [...files];
    newFiles.splice(newFiles.indexOf(file), 1);
    setFiles(newFiles);
    props.onChange(newFiles);
  };

  const style = useMemo(() => ({ ...baseStyle }), []);

  const thumbs = files.map(file => (
    <Grid item sm={12} xs={6} key={file.name}>
      <Grid item xs={12} classes={{ root: classes.thumbContainer }}>
        <DeleteButton onClick={removeFile(file)} className={classes.deleteButton} />
        {file.preview
          ? <img src={file.preview} style={img} alt="Preview" />
          : (
            <Grid container item justify="center" alignItems="center" classes={{ root: classes.fileContainer }}>
              <FileCopy />
              {file.name}
            </Grid>
          )
        }
      </Grid>
    </Grid>
  ));

  useEffect(() => () => {
    files.forEach(file => URL.revokeObjectURL(file.preview));
  }, [files]);

  return (
    <Grid container classes={{ root: classes.paperRoot }}>
      {thumbs}
      {thumbs.length === 0 && (
        <Grid container item sm={12} xs={6}>
          <CardActionArea classes={{ root: classes.root }}>
            <Grid container item justify="center" alignItems="center" {...getRootProps({ style })}>
              <input {...getInputProps()} />
              {label && <Typography>{label}</Typography>}
              {!label && <AddIcon color="primary" fontSize="large" />}
            </Grid>
          </CardActionArea>
        </Grid>
      )}
    </Grid>
  );
}

Dropzone.propTypes = propTypes;
Dropzone.defaultProps = defaultProps;

export default Dropzone;
